<template>
  <div class="kt-portlet kt-portlet--mobile">
    <div class="kt-portlet__head">
      <div class="kt-portlet__head-label">
        <h3 class="kt-portlet__head-title">Scheduled Jobs — Monitoring</h3>
      </div>
      <div class="kt-portlet__head-toolbar">
        <button class="btn btn-primary btn-sm" @click="loadStatus" :disabled="isLoading">
          <i class="fas fa-sync-alt"></i> {{ isLoading ? "กำลังโหลด..." : "Refresh" }}
        </button>
      </div>
    </div>
    <div class="kt-portlet__body">
      <!-- Summary cards -->
      <div class="row mb-3">
        <div class="col">
          <div class="card text-white bg-success">
            <div class="card-body p-2 text-center">
              <h4 class="mb-0">{{ summary.ok || 0 }}</h4>
              <small>OK</small>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="card text-white bg-warning">
            <div class="card-body p-2 text-center">
              <h4 class="mb-0">{{ summary.stale || 0 }}</h4>
              <small>STALE</small>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="card text-white bg-danger">
            <div class="card-body p-2 text-center">
              <h4 class="mb-0">{{ summary.failed || 0 }}</h4>
              <small>FAILED</small>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="card text-white bg-dark">
            <div class="card-body p-2 text-center">
              <h4 class="mb-0">{{ summary.stuck || 0 }}</h4>
              <small>STUCK</small>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="card text-white bg-secondary">
            <div class="card-body p-2 text-center">
              <h4 class="mb-0">{{ summary.neverRan || 0 }}</h4>
              <small>NEVER_RAN</small>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="card text-white bg-info">
            <div class="card-body p-2 text-center">
              <h4 class="mb-0">{{ summary.total || 0 }}</h4>
              <small>Total</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="row mb-3">
        <div class="col-xl-3">
          <label>กลุ่ม (JobGroup):</label>
          <select class="form-control" v-model="filterGroup" @change="loadStatus">
            <option value="">ทั้งหมด</option>
            <option v-for="g in availableGroups" :key="g" :value="g">{{ g }}</option>
          </select>
        </div>
        <div class="col-xl-3">
          <label>Issue:</label>
          <select class="form-control" v-model="filterIssue">
            <option value="">ทั้งหมด</option>
            <option value="OK">OK</option>
            <option value="STALE">STALE</option>
            <option value="FAILED">FAILED</option>
            <option value="STUCK">STUCK</option>
            <option value="NEVER_RAN">NEVER_RAN</option>
          </select>
        </div>
        <div class="col-xl-3">
          <label>สถานะการทำงาน:</label>
          <select class="form-control" v-model="filterActive" @change="loadStatus">
            <option value="active">เปิดใช้งาน (Active)</option>
            <option value="inactive">ปิดอยู่ (Inactive)</option>
            <option value="all">ทั้งหมด</option>
          </select>
        </div>
      </div>

      <!-- Main table -->
      <div class="table-responsive">
        <table class="table table-sm table-striped table-bordered">
          <thead>
            <tr>
              <th class="text-center">#</th>
              <th class="col-jobname">JobName</th>
              <th class="text-center">Group</th>
              <th class="text-center">Trigger</th>
              <th class="text-center">Cron</th>
              <th class="text-center">Last Run</th>
              <th class="text-center">Hrs Since</th>
              <th class="text-center">MaxGap</th>
              <th class="text-center">Last Status</th>
              <th class="text-center">Issue</th>
              <th class="text-center">Active</th>
              <th class="text-center">IsNotify</th>
              <th class="text-center"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredItems.length === 0">
              <td colspan="13" class="text-center text-muted">ไม่พบข้อมูล</td>
            </tr>
            <tr v-for="(it, index) in filteredItems" :key="it.jobName"
                :class="{ 'row-clickable': isManageable(it.jobName) }"
                @click="onRowClick(it)">
              <td class="text-center">{{ index + 1 }}</td>
              <td class="col-jobname">
                <strong>{{ it.jobName }}</strong>
                <i v-if="isManageable(it.jobName)" class="fas fa-cog text-info ml-1" title="Manage"></i>
                <br />
                <small class="text-muted">{{ it.description }}</small>
              </td>
              <td class="text-center"><span class="badge badge-light">{{ it.jobGroup }}</span></td>
              <td class="text-center"><small>{{ it.triggerSource }}</small></td>
              <td class="text-center">
                <span>{{ it.scheduleDesc || formatCron(it.expectedCron) }}</span>
                <br />
                <small class="text-muted"><code>{{ it.expectedCron || "-" }}</code></small>
              </td>
              <td class="text-center">{{ formatDateTime(it.lastStartTime) }}</td>
              <td class="text-center">{{ it.hoursSinceLastRun != null ? it.hoursSinceLastRun : "-" }}</td>
              <td class="text-center">{{ it.maxAllowedGapHours }}</td>
              <td class="text-center">
                <span class="badge" :class="statusBadgeClass(it.lastStatus)">
                  {{ it.lastStatus || "-" }}
                </span>
              </td>
              <td class="text-center">
                <span class="badge" :class="issueBadgeClass(it.issue)">
                  {{ it.issue }}
                </span>
              </td>
              <td class="text-center" @click.stop>
                <!-- CR JobMonitor Phase 1: เปิด/ปิดการทำงานของ job (คนละตัวกับ IsNotify) -->
                <label class="switch mb-0" :title="it.isActive ? 'job ทำงานอยู่' : 'job ถูกปิด — endpoint จะ skip'">
                  <input type="checkbox"
                         :checked="it.isActive"
                         :disabled="togglingActive[it.jobName]"
                         @change="toggleActive(it, $event)" />
                  <span class="slider"></span>
                </label>
              </td>
              <td class="text-center" @click.stop>
                <label class="switch mb-0" title="ส่ง/ไม่ส่งอีเมลแจ้งเตือนเมื่อ job ล้มเหลว">
                  <input type="checkbox"
                         :checked="it.isNotify"
                         :disabled="togglingJobs[it.jobName]"
                         @change="toggleNotify(it, $event)" />
                  <span class="slider"></span>
                </label>
              </td>
              <td class="text-center" @click.stop>
                <button class="btn btn-sm btn-outline-info" @click="openHistory(it)">
                  <i class="fas fa-history"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- History modal -->
      <div v-if="historyJob" class="modal-overlay" @click.self="historyJob = null">
        <div class="modal-box">
          <div class="modal-header">
            <h5>History — {{ historyJob }}</h5>
            <button type="button" class="close" @click="historyJob = null">&times;</button>
          </div>
          <div class="modal-body">
            <div v-if="isLoadingHistory" class="text-center p-3">
              <i class="fas fa-spinner fa-spin"></i> Loading...
            </div>
            <table v-else class="table table-sm table-striped">
              <thead>
                <tr>
                  <th class="text-center">#</th>
                  <th class="text-center">StartTime</th>
                  <th class="text-center">EndTime</th>
                  <th class="text-center">Duration(ms)</th>
                  <th class="text-center">Status</th>
                  <th class="text-center">Rows/Items</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="history.length === 0">
                  <td colspan="7" class="text-center text-muted">ไม่มีประวัติ</td>
                </tr>
                <tr v-for="(h, i) in history" :key="h.logId">
                  <td class="text-center">{{ i + 1 }}</td>
                  <td class="text-center">{{ formatDateTime(h.startTime) }}</td>
                  <td class="text-center">{{ formatDateTime(h.endTime) }}</td>
                  <td class="text-center">{{ h.durationMs != null ? h.durationMs.toLocaleString() : "-" }}</td>
                  <td class="text-center">
                    <span class="badge" :class="statusBadgeClass(h.status)">{{ h.status }}</span>
                  </td>
                  <td class="text-center">
                    <span v-if="h.rowCountSnap != null">{{ h.rowCountSnap.toLocaleString() }}</span>
                    <span v-else-if="h.totalItems != null">
                      {{ h.successItems || 0 }}/{{ h.totalItems }}
                      <span v-if="h.failedItems"> (fail {{ h.failedItems }})</span>
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td><small class="text-danger">{{ h.errorMessage }}</small></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      items: [],
      summary: {},
      filterGroup: "",
      filterIssue: "",
      filterActive: "active",   // CR JobMonitor Phase 1 — default แสดงเฉพาะ job ที่เปิดใช้งาน
      isLoading: false,
      historyJob: null,
      history: [],
      isLoadingHistory: false,
      togglingJobs: {},    // {jobName: true} while toggling notify
      togglingActive: {},  // {jobName: true} while toggling active
    };
  },
  computed: {
    availableGroups() {
      var set = {};
      this.items.forEach(function (i) {
        if (i.jobGroup) set[i.jobGroup] = true;
      });
      return Object.keys(set).sort();
    },
    filteredItems() {
      var self = this;
      return self.items.filter(function (i) {
        if (self.filterIssue && i.issue !== self.filterIssue) return false;
        return true;
      });
    },
  },
  mounted() {
    this.loadStatus();
  },
  methods: {
    loadStatus() {
      var self = this;
      var params = {};
      if (self.filterGroup) params.jobGroup = self.filterGroup;
      params.activeFilter = self.filterActive || "active";
      self.isLoading = true;
      axios
        .get("/Api/Monitor/scheduled_jobs_status", { params: params })
        .then(function (response) {
          self.items = response.data.items || [];
          self.summary = response.data.summary || {};
        })
        .catch(function (error) {
          console.error("Failed to load scheduled jobs status:", error);
        })
        .finally(function () {
          self.isLoading = false;
        });
    },
    openHistory(item) {
      var self = this;
      self.historyJob = item.jobName;
      self.history = [];
      self.isLoadingHistory = true;
      axios
        .get("/Api/Monitor/scheduled_jobs_history", { params: { jobName: item.jobName, top: 50 } })
        .then(function (response) {
          self.history = response.data.rows || [];
        })
        .catch(function (error) {
          console.error("Failed to load history:", error);
        })
        .finally(function () {
          self.isLoadingHistory = false;
        });
    },
    statusBadgeClass(s) {
      if (s === "SUCCESS") return "badge-success";
      if (s === "FAILED") return "badge-danger";
      if (s === "PARTIAL") return "badge-warning";
      if (s === "RUNNING") return "badge-info";
      return "badge-light";
    },
    issueBadgeClass(s) {
      if (s === "OK") return "badge-success";
      if (s === "STALE") return "badge-warning";
      if (s === "FAILED") return "badge-danger";
      if (s === "STUCK") return "badge-dark";
      if (s === "NEVER_RAN") return "badge-secondary";
      return "badge-light";
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "-";
      var d = new Date(dateStr);
      return d.toLocaleDateString("th-TH") + " " + d.toLocaleTimeString("th-TH");
    },
    isManageable(jobName) {
      // job ที่มีหน้าจัดการ/จำลอง trigger ของตัวเอง — เพิ่มชื่อได้เรื่อย ๆ
      return jobName === "Alert/SendEmailReportAgent"
          || jobName === "PMImageRetention/Purge";
    },
    formatCron(cron) {
      if (!cron) return "-";
      var parts = cron.trim().split(/\s+/);
      if (parts.length !== 5) return cron;
      var m = parts[0], h = parts[1], dom = parts[2], mon = parts[3], dow = parts[4];
      var pad = function (s) { return s.length === 1 ? "0" + s : s; };
      var timePart = "";
      if (/^\d+$/.test(h) && /^\d+$/.test(m)) {
        timePart = pad(h) + ":" + pad(m);
      } else {
        timePart = h + ":" + m;
      }
      // Daily
      if (dom === "*" && mon === "*" && dow === "*") {
        return "Daily " + timePart;
      }
      // Monthly last day
      if (dom === "L" && mon === "*" && dow === "*") {
        return "Monthly EOM " + timePart;
      }
      // Monthly specific day
      if (/^\d+$/.test(dom) && mon === "*" && dow === "*") {
        return "Monthly Day " + dom + " " + timePart;
      }
      // Weekly
      if (dom === "*" && mon === "*" && /^\d+$/.test(dow)) {
        var days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        return "Weekly " + (days[parseInt(dow, 10)] || dow) + " " + timePart;
      }
      // Hourly (every hour)
      if (h === "*" && /^\d+$/.test(m)) {
        return "Hourly :" + pad(m);
      }
      return cron;
    },
    // CR JobMonitor Phase 1 — เปิด/ปิดการทำงานของ job
    toggleActive(item, event) {
      const self = this;
      const newValue = event.target.checked;
      const label = newValue ? "เปิดใช้งาน" : "ปิดการทำงาน";

      if (!window.confirm(`${label} job "${item.jobName}" ?`)) {
        event.target.checked = !newValue;
        return;
      }

      self.$set(self.togglingActive, item.jobName, true);
      axios
        .post("/Api/Monitor/scheduled_jobs_toggle_active", null, {
          params: { jobName: item.jobName, isActive: newValue }
        })
        .then(() => {
          item.isActive = newValue;
          // ถ้ากำลังกรองอยู่ แถวนี้อาจต้องหายไป จึงโหลดใหม่
          if (self.filterActive !== "all") self.loadStatus();
        })
        .catch((e) => {
          console.error("toggle active fail", e);
          event.target.checked = !newValue;   // revert
        })
        .then(() => {
          self.$set(self.togglingActive, item.jobName, false);
        });
    },

    toggleNotify(item, event) {
      var self = this;
      var newValue = event.target.checked;
      // optimistic update
      self.$set(self.togglingJobs, item.jobName, true);
      axios
        .post("/Api/Monitor/scheduled_jobs_toggle_notify", null, {
          params: { jobName: item.jobName, isNotify: newValue }
        })
        .then(function () {
          item.isNotify = newValue;
        })
        .catch(function (e) {
          console.error("toggle notify fail", e);
          // revert checkbox if API failed
          event.target.checked = !newValue;
        })
        .finally(function () {
          self.$set(self.togglingJobs, item.jobName, false);
        });
    },
    onRowClick(item) {
      if (!this.isManageable(item.jobName)) return;
      if (item.jobName === "Alert/SendEmailReportAgent") {
        this.$router.push("/Monitoring/SendEmailReportAgent");
      } else if (item.jobName === "PMImageRetention/Purge") {
        this.$router.push("/Monitoring/ClearImage");
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.modal-box {
  background: white;
  width: 90vw;
  max-width: 1200px;
  max-height: 85vh;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
}
.modal-header {
  padding: 12px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.modal-body {
  padding: 16px 20px;
  overflow-y: auto;
}
.card-body h4 {
  font-size: 1.5rem;
}
.row-clickable {
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.row-clickable:hover {
  background-color: #eef6ff !important;
}
.col-jobname {
  max-width: 240px;
  word-break: break-word;
}
/* Toggle switch */
.switch {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 20px;
  margin: 0;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #bbb;
  border-radius: 20px;
  transition: 0.2s;
}
.slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.2s;
}
.switch input:checked + .slider {
  background-color: #28a745;
}
.switch input:checked + .slider:before {
  transform: translateX(18px);
}
.switch input:disabled + .slider {
  opacity: 0.5;
  cursor: wait;
}
</style>



// WEBPACK FOOTER //
// App/Components/Monitoring/MonitoringScheduledJobs.vue